import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    Alert,
    useTheme,
    useMediaQuery,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Home as HomeIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const RegisterForm: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { language, setLanguage } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const validationSchema = yup.object({
        email: yup.string().email(t('Correo electrónico inválido', 'Invalid email')).required(t('El correo electrónico es requerido', 'Email is required')),
        password: yup.string()
            .min(8, t('La contraseña debe tener al menos 8 caracteres', 'Password must be at least 8 characters'))
            .matches(/^[a-zA-Z0-9]+$/, t('La contraseña debe contener solo letras y números', 'Password must contain only letters and numbers'))
            .required(t('La contraseña es requerida', 'Password is required')),
        confirmPassword: yup.string()
            .oneOf([yup.ref('password')], t('Las contraseñas deben coincidir', 'Passwords must match'))
            .required(t('Confirma tu contraseña', 'Confirm your password')),
        full_name: yup.string().required(t('El nombre completo es requerido', 'Full name is required')),
        referral_code: yup.string().optional(),
    });

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            confirmPassword: '',
            full_name: '',
            referral_code: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setError('');
                console.log('📝 Intentando registro con:', values.email);
                console.log('🌐 URL de la API:', process.env.REACT_APP_API_URL || 'http://localhost:8000/api');
                
                // Registrar representante
                const response = await authService.register({
                    email: values.email,
                    password: values.password,
                    full_name: values.full_name,
                    referral_code: values.referral_code || undefined,
                });
                
                console.log('✅ Registro exitoso:', response);
                
                // Iniciar sesión automáticamente
                console.log('🔐 Iniciando login automático...');
                let loginResp;
                try {
                    loginResp = await authService.login({
                        username: values.email,
                        password: values.password,
                    });
                    console.log('✅ Login exitoso:', loginResp);
                } catch (loginError: any) {
                    console.error('❌ Error en login automático:', loginError);
                    throw new Error(`Error en login automático: ${loginError.message || 'Error desconocido'}`);
                }
                
                console.log('🔍 Estructura de loginResp:', {
                    hasAccessToken: !!loginResp.access_token,
                    hasRefreshToken: !!loginResp.refresh_token,
                    hasUser: !!loginResp.user,
                    keys: Object.keys(loginResp)
                });
                
                // Verificar que los tokens se hayan guardado correctamente
                const token = localStorage.getItem('token');
                const refreshToken = localStorage.getItem('refreshToken');
                
                console.log('🔍 Tokens en localStorage después del login:', {
                    token: token ? 'SÍ' : 'NO',
                    refreshToken: refreshToken ? 'SÍ' : 'NO',
                    tokenLength: token?.length || 0,
                    refreshTokenLength: refreshToken?.length || 0
                });
                
                if (!token) {
                    throw new Error('No se pudo obtener el token de autenticación');
                }
                
                console.log('✅ Token guardado correctamente:', token ? 'SÍ' : 'NO');
                console.log('✅ Refresh token guardado:', refreshToken ? 'SÍ' : 'NO');
                
                // Esperar un poco para asegurar que los tokens estén disponibles
                await new Promise((resolve) => setTimeout(resolve, 1000));
                
                console.log('🎉 Registro y login completados exitosamente');
                navigate('/dashboard');
            } catch (error: any) {
                console.error('❌ Error en el registro completo:', error);
                let errorMessage = 'Error al registrar. Intenta de nuevo.';
                
                if (error.response?.status === 422) {
                    errorMessage = 'Datos inválidos. Verifica la información ingresada.';
                } else if (error.response?.status === 409) {
                    errorMessage = 'El correo electrónico ya está registrado.';
                } else if (error.response?.status === 400) {
                    errorMessage = 'Solicitud inválida. Verifica los datos.';
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setError(errorMessage);
            }
        },
    });

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `url(${process.env.PUBLIC_URL}/images/castillobk.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 2, sm: 4 },
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.8) 0%, rgba(78, 205, 196, 0.8) 50%, rgba(69, 183, 209, 0.8) 100%)',
                    zIndex: 1
                }
            }}
        >
            {/* Botón de traducción en la esquina superior izquierda */}
            <IconButton
                onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                sx={{
                    position: 'fixed',
                    top: { xs: 16, sm: 24 },
                    left: { xs: 16, sm: 24 },
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.1)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                }}
            >
                <span style={{ fontSize: '24px' }}>
                    {language === 'es' ? '🇺🇸' : '🇪🇸'}
                </span>
            </IconButton>

            {/* Icono de casa en la esquina superior derecha */}
            <IconButton
                onClick={() => navigate('/')}
                sx={{
                    position: 'fixed',
                    top: { xs: 16, sm: 24 },
                    right: { xs: 16, sm: 24 },
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.1)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                }}
            >
                <HomeIcon sx={{ 
                    fontSize: { xs: 28, sm: 32 }, 
                    color: '#FF6B6B' 
                }} />
            </IconButton>

            <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2, maxWidth: '400px !important' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            padding: { xs: 3, sm: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: '350px',
                            borderRadius: '25px',
                            border: '4px solid white',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                        }}
                    >
                        <Typography 
                            component="h1" 
                            variant={isMobile ? 'h5' : 'h4'}
                            sx={{ 
                                mb: { xs: 2, sm: 3 },
                                fontWeight: 'bold',
                                color: '#FF6B6B',
                                fontFamily: '"Dancing Script", cursive',
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                textAlign: 'center'
                            }}
                        >
                            🏰 {t('Crear Cuenta', 'Create Account')}
                        </Typography>
                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mt: 2, 
                                    width: '100%', 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    borderRadius: '15px',
                                    border: '2px solid #FF6B6B'
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        <Box
                            component="form"
                            onSubmit={formik.handleSubmit}
                            sx={{ mt: 1, width: '100%' }}
                        >
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="full_name"
                                label={t('Nombre Completo', 'Full Name')}
                                name="full_name"
                                autoComplete="name"
                                autoFocus
                                value={formik.values.full_name}
                                onChange={formik.handleChange}
                                error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                                helperText={formik.touched.full_name && formik.errors.full_name}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={t('Correo Electrónico', 'Email')}
                                name="email"
                                autoComplete="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={t('Contraseña', 'Password')}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                autoComplete="new-password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label={t('Confirmar Contraseña', 'Confirm Password')}
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                autoComplete="new-password"
                                value={formik.values.confirmPassword}
                                onChange={formik.handleChange}
                                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                fullWidth
                                id="referral_code"
                                label={t('Código de Referido (Opcional)', 'Referral Code (Optional)')}
                                name="referral_code"
                                placeholder="ABC12345"
                                value={formik.values.referral_code}
                                onChange={formik.handleChange}
                                helperText={t('Si tienes un código de referido, ingrésalo aquí para obtener Tfiats de bienvenida', 'If you have a referral code, enter it here to get welcome Tfiats')}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                            />
                            
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ 
                                    mt: { xs: 2, sm: 3 }, 
                                    mb: { xs: 1.5, sm: 2 },
                                    py: { xs: 1.5, sm: 2 },
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    fontWeight: 'bold',
                                    backgroundColor: '#FF6B6B',
                                    borderRadius: '25px',
                                    border: '3px solid white',
                                    boxShadow: '0 8px 20px rgba(255, 107, 107, 0.4)',
                                    '&:hover': {
                                        backgroundColor: '#FF5252',
                                        transform: 'scale(1.02)',
                                        boxShadow: '0 10px 25px rgba(255, 107, 107, 0.6)'
                                    }
                                }}
                            >
                                🚀 {t('Crear Cuenta', 'Create Account')}
                            </Button>
                            
                            <Button
                                fullWidth
                                variant="text"
                                onClick={() => navigate('/login')}
                                sx={{
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    color: '#4ECDC4',
                                    fontWeight: 'bold',
                                    borderRadius: '15px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                        color: '#45B7D1'
                                    }
                                }}
                            >
                                🎓 {t('¿Ya tienes cuenta? Inicia sesión', 'Already have an account? Login')}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default RegisterForm; 